import React, { useMemo } from 'react';
import clsx from 'clsx';
import { ProcessedJob, PartialOffer } from '../../../types/dentist/calendar';
import { JOB_STATUS } from '../../../constants/dentist/jobStatus';
import styles from './DateStatusMarker.module.scss';

interface DateStatusMarkerProps {
  jobs: ProcessedJob[];
  hasAction: boolean;
  isPast?: boolean;
  hasTarget?: boolean;
  /** @deprecated superseded by expiredOfferCount; use expiredOfferCount > 0 instead. */
  hasExpiredOffer?: boolean;
  /** Total expired offers on this date (rendered as grey count badge in top-left per T2-5764). */
  expiredOfferCount?: number;
}

/**
 * Indicators rendered on a calendar date cell.
 * Top-right: job count / live-offer count / action indicator / unread.
 * Top-left (T2-5764): grey count badge showing the number of expired offers.
 */
const DateStatusMarker: React.FC<DateStatusMarkerProps> = ({
  jobs,
  hasAction,
  isPast,
  hasExpiredOffer,
  expiredOfferCount,
}) => {
  const offerCount = useMemo(() => {
    if (!jobs || jobs.length === 0) return 0;

    const postedJobs = jobs.filter(
      job => job.effectiveStatus === JOB_STATUS.POSTED || job.effectiveStatus === JOB_STATUS.PARTIAL
    );

    if (postedJobs.length === 0) return 0;

    let totalOffers = 0;
    postedJobs.forEach((job) => {
      const partialOffers = job.PartialOffers || job.partialOffers || [];
      // Count valid offers (matching jobProcessing logic)
      const validOffers = partialOffers.filter((offer: PartialOffer) =>
        !offer.isResubmitRequested &&
        offer.status !== 1 && // REJECTED
        offer.status !== 3 && // EXPIRED
        offer.status !== 4    // REMOVED
      ).length;
      totalOffers += validOffers;
    });

    return totalOffers;
  }, [jobs]);

  // Prefer the numeric count; fall back to the legacy boolean if a caller hasn't migrated yet.
  const resolvedExpiredCount = typeof expiredOfferCount === 'number'
    ? expiredOfferCount
    : (hasExpiredOffer ? 1 : 0);

  if ((!jobs || jobs.length === 0) && resolvedExpiredCount === 0) return null;

  const count = jobs?.length ?? 0;

  // Legacy parity: check for unread messages
  const hasUnread = (jobs || []).some((job) => {
    const unreadCount = (job as any).unreadMessageCount;
    return (typeof unreadCount === 'number' && unreadCount > 0) || (job as any).hasMessages === true;
  });

  return (
    <>
      {/* Top-left: grey expired-offer count badge (T2-5764) */}
      {resolvedExpiredCount > 0 && (
        <span
          className={clsx(styles.expiredOfferBadge, isPast && styles.past)}
          title={`${resolvedExpiredCount} expired offer${resolvedExpiredCount === 1 ? '' : 's'}`}
        >
          {resolvedExpiredCount}
        </span>
      )}

      {/* Top-right cluster: live offers, multi-shift count, action/unread */}
      <div className={clsx(styles.markerContainer, isPast && styles.past)}>
        {/* Show offer count badge for posted shifts with offers */}
        {offerCount > 0 && (
          <span className={styles.offerBadge} title={`${offerCount} ${offerCount === 1 ? 'offer' : 'offers'}`}>
            {offerCount}
          </span>
        )}

        {/* Show count badge if more than 1 shift */}
        {count > 1 && (
          <span className={styles.countBadge} title={`${count} shifts`}>
            {count}
          </span>
        )}

        {/* Action indicator */}
        {hasAction && (
          <div className={styles.actionIndicator} title="Action Required">!</div>
        )}

        {/* Unread message indicator */}
        {hasUnread && (
          <div className={styles.unreadIndicator} title="Unread messages">●</div>
        )}
      </div>
    </>
  );
};

export default DateStatusMarker;
